import Phenix from "@phenixrts/sdk";
import ReadOnlySubject from '@phenixrts/sdk/types/rx/ReadOnlySubject';
import http from 'Infrastructure/HttpService/http.service';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'services/redux-store';

import { JoinChannelOptions } from '../Entities/joined-channel-options';
import { JoinedChannel } from '../Entities/joined-channel.entity';
import { SimulcastMediaStream } from '../Entities/media-stream.entity';
import { StreamStatus } from '../Enums/stream-status.enum';

const usePhenixMediaStream = (): {
    streamExistsForAlias: (alias: string) => boolean,
    assignStreamToVideoElementByAlias: (joinChannelOptions: JoinChannelOptions) => void,
    connectToStream: (newJoinedChannel: JoinedChannel, joinChannelOptions: JoinChannelOptions) => Promise<any>,
    forgetMediaStream: (alias: string) => void
} => {
    let [mediaStreams,] = useState<SimulcastMediaStream<ReadOnlySubject<MediaStream>>[]>([]);
    const { bearerToken } = useSelector((state: AppState) => state.account);

    const getStreamByAlias = (alias: string): (SimulcastMediaStream<ReadOnlySubject<MediaStream>> | undefined) => {
        return mediaStreams.find((s) => s.alias === alias);
    }

    const streamExistsForAlias = (alias: string): boolean => {
        const stream = getStreamByAlias(alias);

        if (stream?.stream) {
            return true;
        }

        return false;
    };

    const assignStreamToVideoElementByAlias = (joinChannelOptions: JoinChannelOptions): void => {
        const stream = getStreamByAlias(joinChannelOptions.alias);

        if (stream?.stream) {
            stream.stream.subscribe(mediaStream => {
                joinChannelOptions.videoElement.srcObject = mediaStream;
            });
        }
    }

    const connectToStream = (newJoinedChannel: JoinedChannel, joinChannelOptions: JoinChannelOptions): Promise<any> => {
        return new Promise((resolve: any, reject: any) => {
            http.get(`/api/phenix/token/${joinChannelOptions.alias}`, {
                headers: {
                    Authorization: `Bearer ${bearerToken}`
                }
            }).then(response => {
                if (response.data) {
                    const channel = Phenix.Channels.createChannel({
                        videoElement: joinChannelOptions.videoElement,
                        token: response.data
                    });

                    if (newJoinedChannel.videoElement?.isConnected) {
                        addMediaStream(newJoinedChannel.alias, channel.mediaStream, newJoinedChannel.videoElement?.id);
                        resolve(newJoinedChannel);
                    }
                }
            }).catch(reason => {
                reject(reason);
            });
        });
    }

    const addMediaStream = (alias: string, stream: ReadOnlySubject<MediaStream>, videoId?: string) => {
        if (mediaStreams.some((s) => s.alias === alias)) {
            return;
        }
        const tempMediaStreams = [...mediaStreams];

        mediaStreams = [];
        mediaStreams.push(...tempMediaStreams);
        mediaStreams.push({ alias, stream });
    };

    const forgetMediaStream = (alias: string) => {
        const index = mediaStreams.findIndex((x) => x.alias === (alias));
        if (index > -1)
            mediaStreams.splice(index, 1);
    };

    return { streamExistsForAlias, assignStreamToVideoElementByAlias, connectToStream, forgetMediaStream };
};

export default usePhenixMediaStream;